/**
 * A project's quotations, and the two people it takes to finish one.
 *
 * **One quotation is one piece of agreed work** (the facility, 2026-09-16):
 * one deadline, one ending, one record sent when it ends. A project may hold
 * thousands of them — so this is a paginated table rather than a stack of
 * cards, and the only thing it ever asks the backend for is one page-agnostic
 * `GET /api/projects/{id}/orders`, which is already one query for the quotations
 * and one for the proposals.
 *
 * **`order` in this file is the internal name for what a person reads as a
 * quotation.** The table, the model, the route and every identifier here keep
 * that name — renaming a route is a contract change for one word — so the rule
 * is that no STRING a person sees may say it. Stated once, here, rather than
 * on every line.
 *
 * **Finishing one takes two people (2026-09-16.)** Whoever did the work
 * proposes it and a group admin who is NOT them agrees, because finishing is
 * what sends the billing office the record their invoice is written from — the
 * portal itself bills nobody and must never say it does. The routes for that
 * shipped with the API and nothing reached them: `propose_order_completion`
 * answers 201 and closes nothing, and until this card existed the only sign a
 * proposal had been made was a row in the database. That is not a cosmetic gap
 * — twelve identical pending rows were once made on dev by pressing a button
 * that gave no sign of having worked, so saying "waiting for a group admin to
 * agree" is the feature, not the polish.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import { ROLE_RANK, rankInGroup } from "../auth/roles";
import { Badge, Btn, Card, Field, SR_ONLY, Spinner, Textarea, formatWhen,
         Sheet, SheetActions } from "../components/Shared";
import { PersonLabel } from "../components/Portal";
import { useDisplayTimezone } from "../portalConfig";

// Mirror of ORDER_LIVE_STATUSES in backend/portal/models/quotation.py. A draft
// is live: nothing has been signed yet, but it is still a piece of work
// somebody has to end one way or the other, and `finish_project` refuses to
// archive a project while one is open. Keep the two in step.
const ORDER_LIVE_STATUSES = ["draft", "open"];
const isLive = order => ORDER_LIVE_STATUSES.includes(order.status);

// These states, not `Shared.jsx`'s STATUS_LOOK. That map is written for
// projects and jobs and has no wording for `draft`, `open` or `expired`, so a
// quotation would have fallen through to its grey "print the raw word" branch —
// the silent failure StatusBadge's own comment warns about. Five states, named
// here, and an unknown one still prints itself rather than vanishing.
const ORDER_STATUS = {
  draft:     { label: "Draft",     color: "gray",  glyph: "…" },
  open:      { label: "Open",      color: "green", glyph: "●" },
  completed: { label: "Completed", color: "blue",  glyph: "✓" },
  cancelled: { label: "Cancelled", color: "gray",  glyph: "⊘" },
  expired:   { label: "Expired",   color: "amber", glyph: "!" },
};

// The priced quotation's own states — the document half, shown in its own column.
// Same reasoning; these are QuotationsCard's tones, kept identical on purpose
// so one quotation does not change colour between two cards on the same page.
const QUOTE_TONE = {
  draft: "gray", sent: "blue", accepted: "green",
  rejected: "amber", expired: "gray", withdrawn: "gray",
};

// A project may hold thousands of quotations and the route answers all of them,
// so the paging is here. Twenty-five is what fits a laptop screen without the card
// pushing everything below it off the page.
const PAGE_SIZE = 25;

const label = order => order.number || `#${order.id}`;

function OrderStatus({ status }) {
  const look = ORDER_STATUS[status] ?? { label: status || "unknown", color: "gray", glyph: "" };
  return (
    <Badge color={look.color}>
      {look.glyph && <span aria-hidden="true" style={{ marginRight: 4 }}>{look.glyph}</span>}
      {look.label}
    </Badge>
  );
}

/**
 * How long this quotation has, beside its deadline. **Never a negative number.**
 *
 * The backend sends `days_left` negative once the deadline has passed and a
 * separate `overdue` boolean that is the signal to use: `overdue` is
 * `deadline <= now AND the work is live`, which is the same comparison
 * `_assert_order_open_for_data` refuses an upload on, while `days_left < 0` is
 * one instant looser. A row reading "-3 days" makes the reader do the
 * subtraction and hides the one word they act on, so the count is only ever
 * rendered forwards and the past tense is a word.
 *
 * One that has ended gets no countdown at all. Its deadline is history —
 * finished work, not something anybody has to act on — and "3 days left" beside
 * a completed quotation would be a screen inventing a deadline for work that is
 * done.
 *
 * **And nothing at all where there is no deadline.** The date column beside
 * this one already says "no deadline", and `days_left` is null exactly when
 * `deadline` is — so the branch that printed it here put the same two words in
 * one cell twice (fixed 2026-09-16).
 */
function Countdown({ order }) {
  if (!isLive(order)) return null;
  if (order.deadline == null) return null;
  // `overdue` first, and `days_left < 0` only as the belt: an older response
  // that predates the `overdue` field must still not print a minus sign.
  if (order.overdue || (order.days_left != null && order.days_left < 0))
    return <Badge color="red">Overdue</Badge>;
  // Dated, but the response carried no count: the date is already on screen and
  // doing the subtraction here is how this card and the nightly sweep come to
  // disagree by a day about the same piece of work.
  if (order.days_left == null) return null;
  const urgent = order.days_left <= 2;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color: urgent ? "#92400e" : "#6b7280" }}>
      {order.days_left === 0 ? "due today"
        : `${order.days_left} day${order.days_left === 1 ? "" : "s"} left`}
    </span>
  );
}

/**
 * The one dialog all three decisions go through.
 *
 * One component rather than three because the three differ only in their words
 * and in whether the comment is required — and the part that must not vary is
 * the keyboard contract, which `Sheet` owns through `useModalFocus`: Escape, the focus trap and
 * putting focus back where it came from, together.
 *
 * `comment` is seeded to `""` and never to undefined: a controlled textarea
 * whose value starts undefined warns on its first keystroke, and a React
 * warning fails the test that caused it (setupTests.js).
 */
function DecisionDialog({ title, intro, commentLabel, commentHint, commentRequired = false,
                         confirmLabel, tone = "primary", busy, error, onConfirm, onClose }) {
  const [comment, setComment] = useState("");
  const titleId = "order-decision-title";

  const ready = !busy && (!commentRequired || comment.trim().length > 0);

  return (
    <Sheet labelledBy={titleId} onClose={onClose}>
        <h2 id={titleId} style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700,
                                  color: tone === "danger" ? "#b91c1c" : "#0b6b33" }}>
          {title}
        </h2>
        {/* What the button is about to do, stated BEFORE it is pressed. Saying
            it afterwards is how a two-person approval turns into a surprise. */}
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{intro}</p>

        <Field label={commentLabel} required={commentRequired} hint={commentHint}>
          <Textarea rows={3} value={comment} onChange={e => setComment(e.target.value)} />
        </Field>

        {error && (
          <div role="alert" style={{ marginTop: 14, fontSize: 13, color: "#b91c1c" }}>{error}</div>
        )}

        <SheetActions>
          <Btn variant="secondary" onClick={onClose}>Back</Btn>
          <Btn variant={tone} disabled={!ready} onClick={() => onConfirm(comment)}>
            {busy ? <><Spinner /> Working…</> : confirmLabel}
          </Btn>
        </SheetActions>
    </Sheet>
  );
}

export default function OrdersCard({ project, canManage, onUpdated }) {
  const zone = useDisplayTimezone();
  const user = useAuth()?.user;
  const [orders, setOrders] = useState(null);       // null = still loading
  const [error, setError]   = useState(null);
  const [notice, setNotice] = useState(null);
  const [page, setPage]     = useState(0);
  const [dialog, setDialog] = useState(null);       // { kind, order }
  const [busy, setBusy]     = useState(false);
  const [dialogErr, setDialogErr] = useState(null);

  // The group-admin question, answered by auth/roles.js and never by comparing
  // a role to a string here. `user.role` is a cache of the STRONGEST grant, so
  // an account holding operator AND group admin reads as "group_admin" and an
  // inline comparison is false for exactly the people who run the facility.
  //
  // It mirrors `_require_group_standing(min_role="group_admin")`, which is what
  // the approve route actually enforces — a global admin passes there and so
  // does `rankInGroup` here. The frontend gate may be narrower than the
  // server's; it must never be wider.
  const myRank = rankInGroup(user, project.group_id);
  const isGroupAdmin = myRank !== null && myRank <= ROLE_RANK.group_admin;

  const load = useCallback(
    () => api.get(`/api/projects/${project.id}/orders`)
      .then(rows => { setOrders(rows || []); setError(null); })
      .catch(e => { setOrders([]); setError(e.message); }),
    [project.id]);

  useEffect(() => { setPage(0); load(); }, [load]);

  const closeDialog = useCallback(() => { setDialog(null); setDialogErr(null); }, []);

  const submitDecision = async comment => {
    const { kind, order } = dialog;
    const note = comment.trim();
    setBusy(true);
    setDialogErr(null);
    try {
      if (kind === "finish") {
        // **201, and nothing is closed and nothing is sent.** The response
        // carries the proposal's id and its state, not a project — so there is
        // nothing to hand back to `onUpdated`, and the only honest thing the
        // screen can do is say what is now being waited for.
        await api.post(`/api/projects/${project.id}/orders/${order.id}/complete`,
                       { note: note || null });
        setNotice(`Quotation ${label(order)}: you have proposed that it is finished. `
                  + `Nothing has been sent to the billing office — it is waiting for a group `
                  + `admin to agree, and a group admin who is not you has to be the one.`);
      } else if (kind === "approve") {
        const updated = await api.post(
          `/api/projects/${project.id}/orders/${order.id}/completion/`
          + `${order.pending_completion.id}/approve`, { note: note || null });
        onUpdated?.(updated);
        // **What closed is known; what was sent is not.** `close_order` is
        // deliberately best-effort about the send and hands its caller back
        // `(queued, failed)` — a bundle that will not compose, or an
        // `outbox.TooLarge`, leaves `queued == 0` while the quotation closes
        // anyway — and `approve_order_completion` puts that in the audit log
        // and returns a bare project. So the response this screen holds cannot
        // say the record went, and the 422 before the close only rules out
        // having nowhere to send it. Saying "it has gone" from here was a
        // screen reporting somebody else's success (2026-09-16).
        setNotice(`Quotation ${label(order)} is completed, and its record is on its way to `
                  + `whoever handles the billing. This screen is not told whether that send `
                  + `succeeded — the audit log is what says so either way.`);
      } else {
        const updated = await api.post(`/api/projects/${project.id}/orders/${order.id}/cancel`,
                                       { note });
        onUpdated?.(updated);
        // Same reservation, same reason: `_end_order` closes through the same
        // best-effort `close_order` and answers with a project.
        setNotice(`Quotation ${label(order)} is cancelled, and its record is on its way to `
                  + `whoever handles the billing with your comment, so they know why nothing `
                  + `will be billed for it. This screen is not told whether that send `
                  + `succeeded — the audit log is what says so either way.`);
      }
      setDialog(null);
      await load();
    } catch (e) {
      setDialogErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Live ones first, newest first within each. **A partition, not a sort:** the
  // route already returns them by id descending, and it does that rather than
  // by `created_at` because the backfill of 2026-09-16 stamped a whole
  // facility's rows within the same second. Re-sorting here on any timestamp
  // would hand those ties back to whatever sequence the array happened to be
  // in, and would reorder outright wherever a row was created out of id order.
  const rows = orders || [];
  const ordered = rows.filter(isLive).concat(rows.filter(o => !isLive(o)));
  const pageCount = Math.max(1, Math.ceil(ordered.length / PAGE_SIZE));
  // Clamped rather than trusted: closing the last row on the last page
  // shortens the list under a page number that is still held in state, and an
  // empty table under a "Showing 26–50 of 25" is a screen that looks broken.
  const current = Math.min(page, pageCount - 1);
  const visible = ordered.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  const dialogFor = {
    finish: {
      title: `Propose that quotation ${dialog ? label(dialog.order) : ""} is finished`,
      intro: "This closes nothing and sends nothing. It records that you consider the work "
           + "delivered, and a group admin who is not you then has to agree — agreeing is what "
           + "closes the quotation and sends the record. Whoever handles the billing writes "
           + "the invoice from that record; the portal never writes one.",
      commentLabel: "What was delivered",
      commentHint: "Kept in the log and shown to whoever is asked to agree. Optional, but it is "
                 + "the only thing that tells them what they are agreeing to.",
      commentRequired: false,
      confirmLabel: "Propose it",
      tone: "primary",
    },
    approve: {
      title: `Agree that quotation ${dialog ? label(dialog.order) : ""} is finished`,
      intro: "This closes the quotation and sends its record to whoever handles the billing — "
           + "they write the invoice from it, not the portal. Reopening it afterwards takes an "
           + "admin and a stated reason, so read the proposal before you agree.",
      commentLabel: "Why you agree",
      commentHint: "Kept in the log beside the proposer's own note. Optional.",
      commentRequired: false,
      confirmLabel: "Agree and send",
      tone: "primary",
    },
    cancel: {
      title: `Cancel quotation ${dialog ? label(dialog.order) : ""}`,
      // The sentence this dialog exists for. Beside a pending proposal "Cancel"
      // reads like "refuse it", and it is not: it ends the WORK. The record
      // still goes to the billing office — silence about a cancelled quotation
      // leaves them waiting for an invoice nobody is ever going to write.
      intro: "This cancels the QUOTATION, not the proposal. The work ends here, and the record "
           + "still goes to whoever handles the billing — marked cancelled, with your comment, "
           + "so they know why nothing will be billed for it. Only an admin can take a "
           + "quotation back out of this.",
      commentLabel: "Why it is being cancelled",
      commentHint: "Required. It goes to the billing office with the record and is kept in the "
                 + "log beside whoever proposed that the work was finished.",
      commentRequired: true,
      confirmLabel: "Cancel the quotation",
      tone: "danger",
    },
  }[dialog?.kind || "finish"];

  return (
    <Card style={{ gridColumn: "1 / -1" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between",
                    gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151",
                     textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Quotation progress
        </h3>
        {ordered.length > 0 && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            {ordered.filter(isLive).length} live of {ordered.length}
          </span>
        )}
      </div>
      {/* The heading is not "Quotations": the card above already carries that
          word for the quotations a person writes and sends, and two identical
          headings on one page is the confusion this rename exists to end. Same
          thing, later in its life — priced above, running here. */}
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        One quotation is one piece of agreed work, with its own deadline and its own record.
        Finishing one takes two people — whoever did the work proposes it, and a group admin who
        is not them agrees. Until it is signed the only ending available is Cancel.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, fontSize: 13, color: "#b91c1c" }}>{error}</div>}
      {/* role="status" and not "alert": it reports what just happened rather
          than interrupting, and it is the whole answer to "did that button do
          anything". */}
      {notice && (
        <div role="status" style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 8,
                                    background: "rgba(0,150,64,0.08)",
                                    border: "1px solid rgba(0,150,64,0.30)",
                                    fontSize: 13, color: "#0b6b33", lineHeight: 1.5 }}>
          {notice}
        </div>
      )}

      {orders === null ? (
        <Spinner label="Loading quotations" />
      ) : ordered.length === 0 ? (
        /* What to do next, not an empty table. A project with nothing here is a
           project nobody has quoted yet — a real and common state, and the one
           `?order_state=none` exists to find — so what is missing is a priced
           quotation, and saying so is more use than "Nothing to show".
           `open_order_for` runs inside `create_quotation`, so a row appears as
           soon as the work is priced and not when it is accepted: the card said
           the opposite until 2026-09-16. */
        <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
          No quotations yet. One appears here as soon as the work is priced, so the next step is
          under <strong>Quotations</strong>: price the work, send the quotation, and have every
          required signatory sign it — signing is what starts its clock.
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <caption style={SR_ONLY}>
              This project's quotations, live ones first and newest first within each.
            </caption>
            <thead>
              <tr style={{ textAlign: "left", color: "#6b7280", fontSize: 11,
                           textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {/* "Quotation" then "Priced quotation": one thing, and its
                    document. They carry different numbers — the two share one
                    per-group-per-year counter, so a reissued quotation gets the
                    next number in the same series — and a column headed
                    "Quotation" beside another headed the same would read as two
                    pieces of work. */}
                <th scope="col" style={{ padding: "6px 8px" }}>Quotation</th>
                <th scope="col" style={{ padding: "6px 8px" }}>State</th>
                <th scope="col" style={{ padding: "6px 8px" }}>Deadline</th>
                <th scope="col" style={{ padding: "6px 8px" }}>Priced quotation</th>
                <th scope="col" style={{ padding: "6px 8px" }}>
                  <span style={SR_ONLY}>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map(o => {
                // **A proposal outlives the work it is about**, so a live check
                // and not just a pending one. `sweep_overdue_orders` closes a
                // quotation as `expired` and never touches its pending
                // proposal, and `list_orders` filters proposals on their own
                // state alone — so an ended row still carries one. Agreeing to
                // it rewrites `close_reason` from `expired` to `completed`,
                // moves `closed_at`, and queues a SECOND record for work that
                // already ended; neither `approve_order_completion` nor
                // `close_order` looks at the status, so the gate is here.
                // A proposal on an ended quotation is history, and is shown as
                // history rather than as something to act on.
                const pending = isLive(o) ? o.pending_completion : null;
                const stale   = !isLive(o) && !!o.pending_completion;
                // Never the proposer's own, whatever grants they hold — the
                // server checks exactly this, and an account holding both
                // operator and group_admin would satisfy a rule written only as
                // "is a group admin". Compared by id: `requested_by` is the
                // person as `{name, email}`, for reading, not for comparing.
                const mayAgree = !!pending && isGroupAdmin && pending.requested_by_id !== user?.id;
                return (
                  <tr key={o.id} style={{ borderTop: "1px solid #f3f4f6",
                                          verticalAlign: "top" }}>
                    <td data-label="Quotation" style={{ padding: "10px 8px" }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>
                        {label(o)}
                      </div>
                      <div style={{ color: "#6b7280", marginTop: 2 }}>{o.title || "—"}</div>
                    </td>
                    <td data-label="State" style={{ padding: "10px 8px" }}>
                      <OrderStatus status={o.status} />
                      {o.reopened_count > 0 && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                          reopened {o.reopened_count}×
                        </div>
                      )}
                    </td>
                    <td data-label="Deadline" style={{ padding: "10px 8px", whiteSpace: "nowrap" }}>
                      <div>{o.deadline ? formatWhen(o.deadline, { zone })
                                       : <span style={{ color: "#6b7280" }}>no deadline</span>}</div>
                      <div style={{ marginTop: 4 }}><Countdown order={o} /></div>
                    </td>
                    <td data-label="Priced quotation" style={{ padding: "10px 8px" }}>
                      {o.quotation ? (
                        <>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                            {o.quotation.number || `#${o.quotation.id}`}
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <Badge color={QUOTE_TONE[o.quotation.status] || "gray"}>
                              {o.quotation.status || "unknown"}
                            </Badge>
                          </div>
                          <div style={{ marginTop: 4, color: "#374151" }}>
                            {o.quotation.total == null ? "—"
                              : `${o.quotation.total} ${o.quotation.currency || ""}`.trim()}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "#6b7280" }}>never quoted</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 8px" }}>
                      {/* A proposal that only exists in the database is one
                          nobody acts on, and the work stays open with the
                          billing office hearing nothing. So it is shown here,
                          in the row it is about, with who and when. */}
                      {pending && (
                        <div style={{ marginBottom: 8, padding: "8px 10px", borderRadius: 8,
                                      background: "rgba(217,119,6,0.08)",
                                      border: "1px solid rgba(217,119,6,0.30)",
                                      color: "#92400e", lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 600 }}>
                            Waiting for a group admin to agree
                          </div>
                          <div style={{ fontSize: 12, marginTop: 2 }}>
                            Proposed by {pending.requested_by
                              ? <PersonLabel {...pending.requested_by} /> : "somebody since deleted"}
                            {pending.created_at
                              ? ` on ${formatWhen(pending.created_at, { zone })}` : ""}
                          </div>
                          {pending.note && (
                            <div style={{ fontSize: 12, marginTop: 4, overflowWrap: "anywhere" }}>
                              “{pending.note}”
                            </div>
                          )}
                          {!mayAgree && isGroupAdmin && pending.requested_by_id === user?.id && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                              You proposed this, so somebody else has to agree to it.
                            </div>
                          )}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {/* `open`, not `isLive`: a draft is live and CAN be
                            cancelled, but `propose_order_completion` answers
                            409 for one — nothing has been signed, so there is
                            nothing to finish and nothing to bill. A button that
                            can only fail is worst exactly where this one sat,
                            after somebody has written the delivery note. */}
                        {o.status === "open" && canManage && !pending && (
                          <Btn variant="ghost" style={{ padding: "5px 12px", fontSize: 12 }}
                               onClick={() => { setDialogErr(null); setDialog({ kind: "finish", order: o }); }}>
                            Finish
                          </Btn>
                        )}
                        {mayAgree && (
                          <Btn style={{ padding: "5px 12px", fontSize: 12 }}
                               onClick={() => { setDialogErr(null); setDialog({ kind: "approve", order: o }); }}>
                            Agree
                          </Btn>
                        )}
                        {isLive(o) && canManage && (
                          <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 12 }}
                               onClick={() => { setDialogErr(null); setDialog({ kind: "cancel", order: o }); }}>
                            Cancel
                          </Btn>
                        )}
                        {/* **When it ended, for every ended row; the note only
                            where there is one.** Gated on the note alone, this
                            cell printed nothing whatsoever for exactly the rows
                            nobody chose to end: `sweep_overdue_orders` closes a
                            quotation as expired and passes no note, so the one
                            ending a person did not perform was also the one the
                            screen said nothing about (2026-09-16). `closed_at`
                            is on the wire, every ended row has it, and it is
                            the fact the badge cannot carry.

                            `close_reason` is still NOT printed: it is written
                            from the same value as `status` and cleared with it
                            by `reopen_order`, so the badge in the State column
                            is already that word, and one fact in two places is
                            how the two come to disagree. */}
                        {!isLive(o) && (
                          <span style={{ fontSize: 12, color: "#6b7280", overflowWrap: "anywhere" }}>
                            {o.closed_at ? `Ended ${formatWhen(o.closed_at, { zone })}` : "Ended"}
                            {o.close_note ? ` — “${o.close_note}”` : ""}
                          </span>
                        )}
                        {stale && (
                          <span style={{ fontSize: 12, color: "#92400e", overflowWrap: "anywhere" }}>
                            A proposal to finish it was still waiting when it ended.
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {pageCount > 1 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end",
                          gap: 10, marginTop: 12 }}>
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {current * PAGE_SIZE + 1}–{current * PAGE_SIZE + visible.length} of {ordered.length}
              </span>
              <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 12 }}
                   disabled={current === 0} onClick={() => setPage(current - 1)}>
                Previous
              </Btn>
              <Btn variant="secondary" style={{ padding: "5px 12px", fontSize: 12 }}
                   disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>
                Next
              </Btn>
            </div>
          )}
        </div>
      )}

      {dialog && (
        <DecisionDialog {...dialogFor} busy={busy} error={dialogErr}
                        onConfirm={submitDecision} onClose={closeDialog} />
      )}
    </Card>
  );
}
